import { Injectable, OnDestroy, EventEmitter } from '@angular/core';
import { IdentityService, LicensingService } from 'identity';
import { NavigationService, NetFacadeService } from 'netview';
import { UserClass, UserPermissions, Privilege } from 'UvExplorerServer.Dtos.TS/Auth';
import { NetViewDto, ShareAccessTypes, ShareDto, ShareTypes } from 'UvExplorerServer.Dtos.TS';
import { ConfigurationService } from 'configuration';
import { HttpSettings } from 'DiscoveryEngine.TS/Protocols';
import { Integration } from 'DiscoveryEngine.TS/Core/Integration';


@Injectable()
export class UserPermissionsService implements OnDestroy {

	constructor(private identityService: IdentityService,
		private licensingService: LicensingService,
		private navService: NavigationService,
		private netFacadeService: NetFacadeService,
		private configService: ConfigurationService) {

		return;
	}

	public ngOnDestroy(): void {

	}

	public get canEnableDisablePorts(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.PortsEnableDisable);
	}

	public get canManagePortPoe(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.PortsManagePoe);
	}

	public get canEditPortAliases(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.PortsEditAliases);
	}

	public get canManagePortAccessVlans(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.PortsManageAccessVlans);
	}

	public get canManageTrunkedVlans(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.ManageTrunkedVlans);
	}

	public get canViewConfigs(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.ViewConfigs);
	}

	public get canManageAgents(): boolean {
		return this.isAdminOrOwnerOrHasPrivilege(Privilege.ManageAgents);
	}

	public get canViewJackLookup(): boolean {
		if (this.configService.hasIntegrationTable) {
			let pmSettings: Integration = this.configService.integrationTable.Find("PatchManager");
			if (pmSettings) {
				return (pmSettings.Enabled && pmSettings.HasServerURL && pmSettings.HasUsername && pmSettings.HasPassword);
			}
		}
		return false;
	}

	public get canViewCredentials(): boolean {
		return this.isAdminOrOwnerOrEditShare;
	}

	public get canViewDeviceConfigs(): boolean {
		return (this.canViewConfigs);
	}

	private isAdminOrOwnerOrHasPrivilege(privilege: Privilege): boolean {
		return (this.isAdmin || this.isNetworkOwner || this.hasPrivilege(privilege));
	}

	private get isAdminOrOwnerOrEditShare(): boolean {
		return (this.isAdmin || this.isNetworkOwner || (this.shareAccessType == ShareAccessTypes.Edit));
	}

	private get isAdminOrOwner(): boolean {
		return (this.isAdmin || this.isNetworkOwner);
	}

	private get isAdmin(): boolean {
		let userPermissions: UserPermissions = this.identityService.userPermissions;
		if (userPermissions) {
			if (userPermissions.EffectiveUserClass == UserClass.Administrator) {
				// Administrator
				return true;
			}
		}

		return false;
	}

	private get isNetworkOwner(): boolean {
		let userPermissions: UserPermissions = this.identityService.userPermissions;
		if (userPermissions) {
			if (this.navService.location.hasNetViewGuid) {
				let netView: NetViewDto = this.netFacadeService.findNetwork(this.navService.location.netViewGuid);
				if (netView) {
					if (netView.UserGuid == userPermissions.UserGuid) {
						// Network owner
						return true;
					}
				}
			}
		}

		return false;
	}

	private get shareAccessType(): string {
		let userPermissions: UserPermissions = this.identityService.userPermissions;
		if (userPermissions) {
			if (this.navService.location.hasNetViewGuid) {
				let netView: NetViewDto = this.netFacadeService.findNetwork(this.navService.location.netViewGuid);
				if (netView) {
					let highestAccessType: string = ShareAccessTypes.None;

					userPermissions.EffectiveTargetShares.ForEach((share: ShareDto) => {
						if ((share.ShareType == ShareTypes.Network) && (share.SharedItemGuid == netView.Guid)) {
							if (this.shareAccessTypeOrdinal(share.AccessType) > this.shareAccessTypeOrdinal(highestAccessType)) {
								highestAccessType = share.AccessType;
							}
						}
					});

					return highestAccessType;
				}
			}
		}

		return ShareAccessTypes.None;
	}

	private shareAccessTypeOrdinal(accessType: string): number {
		switch (accessType) {
			case ShareAccessTypes.None:
				return 0;
			case ShareAccessTypes.View:
				return 1;
			case ShareAccessTypes.Edit:
				return 2;
			default:
				console.assert(false);
				return -1;
		}
	}

	private hasPrivilege(privilege: Privilege): boolean {
		let userPermissions: UserPermissions = this.identityService.userPermissions;
		if (userPermissions) {
			if (userPermissions.EffectivePrivileges.Contains(privilege)) {
				return true;
			}

			if (this.navService.location.hasNetViewGuid) {
				let netView: NetViewDto = this.netFacadeService.findNetwork(this.navService.location.netViewGuid);
				if (netView) {
					if (userPermissions.EffectiveTargetShares.Some((share: ShareDto) => {
						if ((share.ShareType == ShareTypes.Network) && (share.SharedItemGuid == netView.Guid)) {
							return share.Privileges.Contains(privilege);
						}
						else {
							return false;
						}
					})) {
						return true;
					}
				}
			}
		}

		return false;
	}

}
